import React, {
  useCallback,
  useState,
  useMemo,
  useRef,
  useEffect,
} from 'react';

import moment from 'moment';
import { useIsFetching } from '@tanstack/react-query';
import dynamic from 'next/dynamic';

import Typography from '@components/Typography';
import CustomTooltip from '@components/ui/Tooltip';
import {
  ProfileReservation,
  PlusIcon,
  ArrowDownIcon,
  ChevronUp,
  CheckMark,
} from '@assets/icons';
import { Col, FlexibleDiv, Row } from '@components/formStyles';
import CustomIcon from '@components/utils/CustomIcon';
import { TourAvailabilityStatusType } from '@api/index';
import FlexContainer from '@components/utils/FlexContainer';
import Markdown from '@components/utils/Markdown';
import {
  useTourDetailVariationContext,
  TourRoomProps,
  SelectedVariation,
} from '@contexts/tour-detail/TourDetail';
import { useOutsideClick } from '@hooks/hooks';
import Tooltip from '@components/ui/Tooltip/next';
import {
  callRequestGtm,
  callRequestSubmitGtm,
} from '@utils/gtm/call-request-gtm';
import { generateUUID } from '@utils/generateUUID';

import TourDateItem from '../TourDateItem';
import { RoomDetailBoxDesktop } from '../RoomDetailBox';
import {
  WrapperDesktop,
  BoxWrapperDesktop,
  StyledButtonDesktop,
  ActionsDesktop,
  Count,
  Line,
  TourCardDatesItem,
  TourCardDatesWrapper,
  TourPointsDate,
  TourPointsText,
  DesktopTourCardDatesPoints,
  Text,
  StyledInfoModal,
  TitleName,
  NotAvailabilityText,
  DiscountedDiv,
  NewRoomButton,
  NewRoomButtonLabel,
  PriceBoxWrapperDesktop,
} from '../styled';
import DiscountedBox from '../DiscountedBox';
import TourSelectionRoom from '../TourSelectionRoom';
import { HandlersConfig, TourSelectionCardProps } from './props';
import {
  getButtonConfig,
  shouldDeleteAllTourPackageRooms,
  TourTypesSalesItemMap,
} from './helpers';

const LetUsCallYou = dynamic(
  () => import('@components/ui/LetUsCallYou/LetUsCallYou')
);

const DesktopTourSelectionCard = ({
  full,
  price1,
  price2,
  price3,
  price1Discounted,
  price2Discounted,
  price3Discounted,
  childPriceList = [],
  dates,
  hideQuotaInfo,
  packageId,
  packageDetailId,
  companyCommissionAmount,
  days,
  status,
  checkInDate,
  checkOutDate,
  entityName = '',
  tourType,
  currencyName,
  tourId,
  data,
  personQuota,
  pagesName,
  departureDate,
  departureText,
  arrivalDate,
  arrivalText,
  departurePointList,
  cashDiscountType,
  cashDiscountRateAmountValue,
  cashDiscountRateAmountType,
}: TourSelectionCardProps) => {
  const newRoomButtonRef = useRef<HTMLButtonElement>(null);
  const [showLetUsCallYou, setShowLetUsCallYou] = useState<boolean>(false);
  const [visibleRoomTooltip, setVisibleRoomTooltip] = useState<boolean>(false);
  const [visibleRoomPackage, setVisibleRoomPackage] = useState<boolean>(false);

  const [guestInfoModalText, setGuestInfoModalText] = useState('');
  const isLoadingVariations = useIsFetching(['tour-precalculate']);
  const {
    tourPackages,
    addPackageRoom,
    removePackageRoom,
    removePackageRooms,
  } = useTourDetailVariationContext();

  useOutsideClick(newRoomButtonRef, () => {
    if (visibleRoomTooltip) {
      toggleRoomTooltip();
    }
  });

  const currentPackage = tourPackages?.find(
    (packageItem) => packageItem.packageDetailId === packageDetailId
  );

  // ===================================
  //  Toggle the rooms limit tooltip
  // ===================================
  const toggleRoomTooltip = () => setVisibleRoomTooltip((val) => !val);

  // ===================================
  //  Toggle the rooms package card
  // ===================================
  const toggleRoomPackage = () => setVisibleRoomPackage((val) => !val);

  // ===================================
  //  Add new tour room
  // ===================================
  const handleAddNewTourRoom = () => {
    if (currentPackage?.rooms?.length === 3) return;

    const newRoom: TourRoomProps = {
      roomId: generateUUID(),
      guests: {
        id: 1,
        adultNumber: 1,
        childList: [],
      },
    };

    addPackageRoom(packageDetailId ?? -1, newRoom);
  };

  // ===================================
  //  Remove tour room
  // ===================================
  const removeTourRoom = (roomId: string, index: number) => {
    const shouldDeleteAllRooms = shouldDeleteAllTourPackageRooms(
      index,
      currentPackage
    );

    if (shouldDeleteAllRooms) return removePackageRooms(packageDetailId ?? -1);

    removePackageRoom(packageDetailId ?? -1, roomId);
  };

  // ===================================
  //  Toggle the let us call you modal
  // ===================================
  const toggleLetUsCallYou = useCallback(
    () => setShowLetUsCallYou((state) => !state),
    []
  );

  // ===================================
  //  Handle buttons config
  // ===================================
  const handlersConfig = useMemo<HandlersConfig>(
    () => ({
      defaultHandler: () => {
        const selectedPackage = data?.packages.find(
          (pkg) => pkg.tourPackageDetailId === packageDetailId
        );
        callRequestGtm(entityName + ' - ' + selectedPackage?.tourPackageName);

        toggleLetUsCallYou();
      },
      [TourAvailabilityStatusType.AVAILABLE]: () => {
        if (full) return;
        if (!currentPackage?.rooms || currentPackage?.rooms?.length === 0) {
          handleAddNewTourRoom();
          toggleRoomPackage();
        } else {
          removePackageRooms(packageDetailId);
          toggleRoomPackage();
        }
      },
    }),
    [toggleLetUsCallYou, currentPackage?.rooms]
  );

  const buttonConfig = getButtonConfig(handlersConfig, status, full);

  useEffect(() => {
    if (!currentPackage?.rooms || currentPackage?.rooms?.length === 0) {
      setVisibleRoomPackage(false);
    }
  }, [currentPackage]);

  // ======================================
  //  Check if package selected
  // ======================================
  const isSelected = useMemo(() => {
    const packageSelectedVariations: SelectedVariation[] = [];
    currentPackage?.rooms?.map((item) =>
      item?.selectedVariationItems?.map((variation) =>
        packageSelectedVariations.push(variation)
      )
    );

    return !!packageSelectedVariations?.find(
      (selected) => selected.packageDetailId === packageDetailId
    );
  }, [tourPackages]);

  return (
    <WrapperDesktop>
      <Row>
        <CustomIcon
          icon={ProfileReservation}
          fontSize={29}
          paletteColor='coral'
        />
        <FlexibleDiv left={10} top={-7}>
          <Col>
            <Tooltip
              overlayInnerStyle={{
                position: 'relative',
                borderRadius: 8,
                width: 'auto',
                backgroundColor: '#292a2c',
                fontSize: 14,
                color: 'white',
                lineHeight: 1.43,
                textAlign: 'left',
                padding: '10px',
              }}
              placement='topLeft'
              title={pagesName}
            >
              <TitleName>{pagesName}</TitleName>
            </Tooltip>
            <Typography variant='Title5' color='D400' weight='Bold'>
              {dates}
            </Typography>

            <Row alignItems='center'>
              {days ? (
                <Typography variant='Title5' color='D400' weight='Regular'>
                  {days?.dayCount} gün {days?.nightCount} gece
                </Typography>
              ) : null}
              {personQuota && !hideQuotaInfo && personQuota < 10 ? (
                <Count>Son {personQuota} Kişi</Count>
              ) : null}
            </Row>
          </Col>
        </FlexibleDiv>
      </Row>

      {cashDiscountRateAmountValue ? (
        <DiscountedDiv>
          <DiscountedBox
            currencyName={currencyName}
            type={cashDiscountType}
            rate={cashDiscountRateAmountValue}
            rateType={cashDiscountRateAmountType}
          />
        </DiscountedDiv>
      ) : null}

      <FlexibleDiv top={20}>
        <PriceBoxWrapperDesktop>
          <BoxWrapperDesktop>
            {price1 && price1Discounted ? (
              <RoomDetailBoxDesktop
                title='Tek Kişilik Oda'
                price={price1}
                discountPrice={price1Discounted}
                currency={currencyName}
              />
            ) : null}
            {price1 && !price1Discounted ? (
              <RoomDetailBoxDesktop
                title='Tek Kişilik Oda'
                price={price1}
                currency={currencyName}
              />
            ) : null}
            {price2 && price2Discounted ? (
              <RoomDetailBoxDesktop
                title='İki Kişilik Oda'
                price={price2}
                discountPrice={price2Discounted}
                currency={currencyName}
              />
            ) : null}
            {price2 && !price2Discounted ? (
              <RoomDetailBoxDesktop
                title='İki Kişilik Oda'
                price={price2}
                currency={currencyName}
              />
            ) : null}
            {price3 && price3Discounted ? (
              <RoomDetailBoxDesktop
                title='Üç Kişilik Oda'
                price={price3}
                discountPrice={price3Discounted}
                currency={currencyName}
              />
            ) : null}
            {price3 && !price3Discounted ? (
              <RoomDetailBoxDesktop
                title='Üç Kişilik Oda'
                price={price3}
                currency={currencyName}
              />
            ) : null}
            {childPriceList?.map((childPrice, index) =>
              childPrice.discountedPrice &&
              cashDiscountType === 'PerRoom' &&
              cashDiscountRateAmountType === 'Value' ? (
                <RoomDetailBoxDesktop
                  key={childPrice.startAge}
                  title={`${childPrice.childOrder}. Çocuk`}
                  price={childPrice.price}
                  subText={`${childPrice.startAge}-${childPrice.endAge} Yaş`}
                  currency={currencyName}
                />
              ) : (
                <RoomDetailBoxDesktop
                  key={index}
                  title={`${childPrice.childOrder}. Çocuk`}
                  price={childPrice.price}
                  subText={`${childPrice.startAge}-${childPrice.endAge} Yaş`}
                  currency={currencyName}
                  discountPrice={childPrice.discountedPrice}
                />
              )
            )}
          </BoxWrapperDesktop>

          <ActionsDesktop>
            {!full && personQuota == 0 ? (
              <NotAvailabilityText>
                Müsaitlik Bulunmamaktadır.
              </NotAvailabilityText>
            ) : (
              <StyledButtonDesktop
                onClick={buttonConfig.handler}
                selected={isSelected}
                variant='Primary'
                block
              >
                {isSelected ? 'Tur Seçildi' : buttonConfig.label}

                {status === TourAvailabilityStatusType.AVAILABLE && !full ? (
                  <FlexibleDiv left={10}>
                    <CustomIcon
                      icon={
                        isSelected
                          ? CheckMark
                          : visibleRoomPackage
                          ? ChevronUp
                          : ArrowDownIcon
                      }
                      paletteColor={isSelected ? 'greenSuccess' : 'white'}
                      height={isSelected ? 11 : 20}
                      width={24}
                    />
                  </FlexibleDiv>
                ) : null}
              </StyledButtonDesktop>
            )}
          </ActionsDesktop>
        </PriceBoxWrapperDesktop>
      </FlexibleDiv>
      {currentPackage?.rooms?.length ? (
        <>
          <Line style={{ zIndex: 1, position: 'relative' }} />
          <FlexContainer style={{ flexWrap: 'wrap', gap: '30px' }}>
            {currentPackage?.rooms?.map((room, index) => (
              <TourSelectionRoom
                key={room.roomId}
                roomId={room.roomId}
                roomIndex={index}
                removeRoom={removeTourRoom}
                title='Aşağıdaki opsiyonlardan birini seçerek ilerleyiniz.'
                packageId={packageId}
                personQuota={personQuota}
                tourId={data?.id}
                price3={price3}
                packageDetailId={packageDetailId}
                day={days?.dayCount}
                nightCount={days?.nightCount}
                companyCommissionAmount={companyCommissionAmount}
                selectedVariationTitle={dates}
                type={tourType}
              />
            ))}
          </FlexContainer>

          <CustomTooltip
            title='İncelediğiniz turda 3 oda için müsaitlik bulunmaktadır.'
            placement='top'
            hideTooltip={() => setVisibleRoomTooltip(false)}
            open={Boolean(
              currentPackage?.rooms?.length === 3 &&
                visibleRoomTooltip &&
                !isLoadingVariations
            )}
          >
            <NewRoomButton
              onClick={handleAddNewTourRoom}
              isDisabled={Boolean(currentPackage?.rooms?.length === 3)}
              onMouseEnter={toggleRoomTooltip}
              onMouseLeave={toggleRoomTooltip}
              onMouseDown={undefined}
              ref={newRoomButtonRef}
            >
              <CustomIcon icon={PlusIcon} fontSize={20} paletteColor='white' />

              <NewRoomButtonLabel>Oda Ekle</NewRoomButtonLabel>
            </NewRoomButton>
          </CustomTooltip>
        </>
      ) : null}

      {guestInfoModalText ? (
        <StyledInfoModal
          title={
            <Text size={25} bold>
              Uyarı
            </Text>
          }
          content={<span>{guestInfoModalText}</span>}
          onCancel={() => setGuestInfoModalText('')}
        />
      ) : null}

      {checkInDate && checkOutDate ? (
        <LetUsCallYou
          visibleLetUsCallYouModal={showLetUsCallYou}
          token=''
          onClose={toggleLetUsCallYou}
          entityId={tourId.toString()}
          entityName={entityName}
          currency={currencyName}
          price={price3 || 0}
          salesItemType={TourTypesSalesItemMap[tourType]}
          formCheckInDate={moment(checkInDate) || null}
          formCheckOutDate={moment(checkOutDate) || null}
          onFormSendSuccess={() => {
            alert('Form gönderildi');
            callRequestSubmitGtm(entityName, currencyName!, price3 || 0);
          }}
          hotelRoomList={[]}
        />
      ) : null}

      <TourCardDatesWrapper>
        <TourCardDatesItem>
          <TourDateItem label='Gidiş Tarihi' date={departureDate || ''} />
          <Markdown text={departureText} />
        </TourCardDatesItem>

        <TourCardDatesItem>
          <TourDateItem label='Dönüş Tarihi' date={arrivalDate || ''} />
          <Markdown text={arrivalText} />
        </TourCardDatesItem>
      </TourCardDatesWrapper>

      <DesktopTourCardDatesPoints>
        <FlexibleDiv bottom={10}>
          <Typography variant='Title5' color='D400' weight='Bold'>
            Kalkış Noktaları
          </Typography>
        </FlexibleDiv>
        <Row>
          {departurePointList?.map((item) => (
            <FlexibleDiv right={31} key={item.tourDeparturePointId}>
              <TourPointsDate>
                <Typography variant='caption1' color='D400' weight='Bold'>
                  {item.departureTime}
                </Typography>
              </TourPointsDate>
              <TourPointsText>
                <Typography variant='caption1' color='D400'>
                  {item.description}
                </Typography>
              </TourPointsText>
            </FlexibleDiv>
          ))}
        </Row>
      </DesktopTourCardDatesPoints>
    </WrapperDesktop>
  );
};

export default DesktopTourSelectionCard;
